import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CiBookmark } from 'react-icons/ci';
import { IoIosArrowDown } from 'react-icons/io';
import { IoIosArrowUp } from 'react-icons/io';
import { HiMiniQuestionMarkCircle } from 'react-icons/hi2';
import { DocumentSearchIcon } from '@svgs';
import { CustomTable, HandleInputToggle, HandleAuthPopup } from '@components';
import { useSelector } from 'react-redux';
import { RootState } from '@store';
import { CustomSpinner } from '@components';
import { debounce, toastSuccessHelper } from '@utils';
import { ImBookmark } from 'react-icons/im';

import {
	transformAcrisData,
	transformEvictionsData,
	transformHPDComplentesData,
	transformHPDViolationsData,
	transformDOBComplentesData,
	transformDOBViolationsData,
	transformECBViolationsData,
	transformdobFiledPermitsData,
	transformdobIssuedPermitsData,
	transformHousingLitigationsData,
	getDatasetMetadata,
} from '@utils';
import { tabData, tabHeading } from '@mockData';
import { DatasetMetadata } from '@interface';
import {
	addPropertyBookMarked,
	removePropertyBookMarked,
	getListPropertyBookMarked,
} from '@services';

export const PropertyCard: React.FC = () => {
	const [isTabOpen, setIsTabOpen] = useState(true);
	const [isInputOpen, setIsInputOpen] = useState(false);
	const [isAuthModelOpen, setIsAuthModelOpen] = useState(false);
	const [selectTab, setSelectTab] = useState<string>('salesAndFinancing');
	const [tableData, setTableData] = useState<Array<any>>([]);
	const [isPropertyMarked, setIsPropertyMarked] = useState<boolean>(false);
	const [lodder, setLodder] = useState<boolean>(false);
	const [dataSet, setDataSet] = useState<DatasetMetadata>({
		model_name: '',
		records_start: '',
		records_end: '',
		last_update: '',
		update_schedule: '',
	});
	const propertyData = useSelector(
		(state: RootState) => state.rootSlice.property
	);
	const { isLoggedIn } = useSelector(
		(state: RootState) => state.rootSlice.application
	);
	const {
		addressState: {
			properties: {
				name,
				borough,
				postalcode,
				addendum: {
					pad: { bbl },
				},
			},
		},
	} = useSelector((state: RootState) => state.rootSlice.application);

	const navigateToProperty = () => {
		setIsInputOpen(false);
	};

	const modalRef = useRef<HTMLDivElement | null>(null);
	const openModal = () => setIsAuthModelOpen(true);
	const closeModal = () => setIsAuthModelOpen(false);

	useEffect(() => {
		const property = propertyData[selectTab as keyof typeof propertyData];
		const data = property?.data ?? [];
		if (selectTab === 'salesAndFinancing')
			setTableData(transformAcrisData(data));
		handlePropertyDataset();
	}, [propertyData]);

	useEffect(() => {
		// if (localStorage.getItem(AppString.user_token)) {
		// 	setIsLoggedIn(true);
		// }
		const handleClickOutside = (event: MouseEvent) => {
			if (
				modalRef.current &&
				!modalRef.current.contains(event.target as Node)
			) {
				closeModal();
			}
		};

		if (isAuthModelOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isAuthModelOpen]);

	const handleLookupTab = () => {
		setIsTabOpen(!isTabOpen);
	};

	const handleSearchToggle = () => {
		setIsInputOpen(!isInputOpen);
	};

	const handlePropertyDataset = () => {
		const datasetObject = propertyData['propertyDataset'];
		if (datasetObject?.data) {
			const obj = getDatasetMetadata(datasetObject?.data, selectTab);
			obj && setDataSet(obj);
		}
	};

	const transformFunctions: Record<string, (data: any[]) => any[]> = {
		salesAndFinancing: transformAcrisData,
		marshalEvictions: transformEvictionsData,
		hpdComplaints: transformHPDComplentesData,
		hpdViolations: transformHPDViolationsData,
		dobComplaints: transformDOBComplentesData,
		dobViolations: transformDOBViolationsData,
		ecbViolations: transformECBViolationsData,
		dobFiledPermits: transformdobFiledPermitsData,
		dobIssuedPermits: transformdobIssuedPermitsData,
		housingLitigations: transformHousingLitigationsData,
	};

	const handlePropertyTab = (dataValue: any[], key: string) => {
		setSelectTab(key);
		handlePropertyDataset();
		const transformFunction = transformFunctions[key] || ((data) => data);
		setTableData(transformFunction(dataValue));
	};

	const checkPropertyMarked = useCallback(async () => {
		try {
			setIsPropertyMarked(false);
			const response = await getListPropertyBookMarked(bbl);
			if (response.status === 200 && response?.data?.data?.length > 0) {
				setIsPropertyMarked(true);
			}
		} catch (e) {
			console.log(e);
		}
	}, [bbl]);

	useEffect(() => {
		if (isLoggedIn) {
			checkPropertyMarked();
		} else {
			setIsPropertyMarked(false);
		}
	}, [isLoggedIn]);

	const addPropertyBookMark = useCallback(async () => {
		setLodder(true);
		try {
			const response = await addPropertyBookMarked({
				bbl,
				name,
				borough,
				postalcode,
			});
			if (response.status === 200) {
				setIsPropertyMarked(true);
				toastSuccessHelper('Successfully bookmarked this property!');
			}
		} catch (e: any) {
			e?.response?.data?.message || 'Something went wrong';
		}
		setLodder(false);
	}, [bbl, name, borough, postalcode]);

	const debouncedFetchResults = useCallback(
		debounce(addPropertyBookMark, 300),
		[addPropertyBookMark]
	);

	const handleBookmarkedProperty = async () => {
		if (isPropertyMarked) {
			const result = window.confirm(
				'Are you sure you want to delete this bookmark?'
			);
			if (result) {
				setLodder(true);
				try {
					const response = await removePropertyBookMarked(bbl);
					if (response.status === 200) {
						setIsPropertyMarked(false);
						toastSuccessHelper('Successfully deleted this bookmark!');
					}
				} catch (e: any) {
					e?.response?.data?.message || 'Something went wrong';
				}
				setLodder(false);
			}
		} else {
			debouncedFetchResults();
		}
	};

	return (
		<div className="max-w-full mx-auto py-6 px-8">
			<div className="mb-4 flex justify-between">
				<div>
					<HandleInputToggle
						isInputOpen={isInputOpen}
						handleSearchToggle={handleSearchToggle}
						navigaterFunction={navigateToProperty}
					/>
				</div>
				<div className="flex items-center space-x-2">
					{lodder && <CustomSpinner width={25} height={25} border={4} />}

					<button
						onClick={!isLoggedIn ? openModal : handleBookmarkedProperty}
						disabled={lodder}
						className=" hover:underline inline-flex space-x-1 items-center"
					>
						{isPropertyMarked ? (
							<ImBookmark className="h-7 w-7" />
						) : (
							<CiBookmark className="h-7 w-7" />
						)}
						<span className="hidden md:inline">
							{isLoggedIn
								? isPropertyMarked
									? 'You have bookmarked this property'
									: 'Bookmark this property'
								: 'Login to bookmark this property'}
						</span>
					</button>
				</div>
			</div>
			<div className="mb-5 text-[11px] space-x-1">
				<button onClick={handleLookupTab} className="text-[#014c8d] font-bold">
					<div className="inline-flex items-center">
						<span>{isTabOpen ? 'Collapse' : 'Expand Datasets'}</span>
						<span>
							{isTabOpen ? (
								<IoIosArrowDown className="h-4 w-4" />
							) : (
								<IoIosArrowUp className="h-4 w-4" />
							)}
						</span>
					</div>
				</button>
				{isTabOpen && (
					<span>Click to view different data about this property</span>
				)}
			</div>
			{/* Tabs Section */}
			<div
				className={`lookup-tabs__tabs ${isTabOpen ? 'lookup-open' : 'hidden'}`}
			>
				{tabData.map(({ label, key }, index) => {
					const property = propertyData[key as keyof typeof propertyData];
					const data = property?.data ?? [];
					const loading = property?.loading ?? false;

					return (
						<button
							key={index}
							className={`lookup-table-tab tab--primary relative ${selectTab === key ? 'tab--primary--active' : ''} `}
						>
							<div
								onClick={() => handlePropertyTab(data, key)}
								className="w-full h-full flex justify-between items-center"
							>
								<div>
									{!loading ? label.replace('...', data.length) : label}
								</div>
								{loading && (
									<div>
										<CustomSpinner width={17} height={17} />
									</div>
								)}
							</div>
						</button>
					);
				})}
			</div>

			{/* Sales Section */}
			<div className="pt-4 font-bold-400">
				<div className="inline-flex items-center space-x-2">
					<h2 className="text-lg font-semibold  ">
						{tabHeading[selectTab as keyof typeof tabHeading]}
					</h2>
					<HiMiniQuestionMarkCircle className="h-6 w-6 text-[#324B69] hover:text-[#b9b8b8] cursor-pointer" />
					<span className="bg-[#d8e3f1] text-[75%] font-bold py-[0.25em] px-[0.4em]">
						Tax Lot Data
					</span>
				</div>

				<p className="text-sm   mb-2">
					This datasets range is from <b>{dataSet.records_start}</b> to{' '}
					<b>{dataSet.records_end}</b>. It was last updated on{' '}
					<b>{dataSet.last_update}</b> and is on an automated{' '}
					{dataSet.update_schedule} update schedule.
				</p>

				{selectTab === 'salesAndFinancing' && (
					<div>
						<p className="text-sm   mt-2">
							Click the Document ID to view the full record and the document
							icon{' '}
							<span className="inline-block bg-gray-300 rounded-full w-5 h-5 text-center">
								<img src={DocumentSearchIcon} alt="Document Icon" />
							</span>{' '}
							to view the scanned document.
						</p>
					</div>
				)}
			</div>
			<div>
				<CustomTable
					data={tableData}
					headerClass="bg-[#a2cffc]"
					selectTab={selectTab}
				/>
			</div>
			<div>
				{isAuthModelOpen && (
					<HandleAuthPopup closeModal={closeModal} modelref={modalRef} />
				)}
			</div>
		</div>
	);
};
